import { Timeslot } from '@raskrask-dk/bookingflow-state-machine'
import { RRDate } from '@raskrask-dk/raskrask-utils'
import {
    Grid,
    Flex,
    Button,
    ProductModal,
    Text,
} from '@raskrask-dk/rr-components'
import { TimeSlot } from '@raskrask-dk/rr-components/lib/Organisms/ProductModal/Tabs/Book/props'
import { t } from 'i18next'
import { ReactElement, RefObject } from 'react'
import Slider from 'react-slick'
import { BasisTypeBF, GoalBF, PartnerBF, ServiceBF } from '../../../../models'
import { useAppDispatch } from '../../../../redux/hooks'
import { setActiveTab } from '../../../../redux/reducers/calendarSettings'
import { stripHTML } from '../../../../services/stripHTML'
import { personalTrainingTabsMaker } from './tabPersonalTraining'
// import { focusTab } from './tabPersonalTraining'

export const MapTimeSlot = (
    timeSlot: Timeslot,
    isSelected: boolean
): TimeSlot => {
    return {
        time: timeSlot.start,
        state: isSelected ? 'selected' : undefined,
    }
}

export const PartnerModalComponent = ({
    activeTab,
    selectedGoal,
    selectedPartner,
    selectedService,
    selectedDate,
    time,
    onClickBook,
    canClickNext,
    onClickKlippeKortButtonHeaderDesktop,
    partnerModalCarouselRef,
    dates,
    isMobile,
    setDate,
    timeSlots,
    setTime,
    handleOnCloseModal,
    handleOnOpenModal,
    basisType,
    onScrollEvent,
    isCompanyBooking,
}: {
    activeTab: string
    selectedGoal: GoalBF
    selectedPartner: PartnerBF
    selectedService: ServiceBF
    selectedDate: RRDate | undefined
    time: string
    onClickBook: () => void
    canClickNext: boolean
    onClickKlippeKortButtonHeaderDesktop: () => void
    partnerModalCarouselRef: RefObject<Slider>
    dates: CarouselDate[] | undefined
    isMobile: boolean
    setDate: (date: string) => void
    timeSlots: TimeSlot[]
    setTime: (time: string) => void
    handleOnCloseModal: () => void
    handleOnOpenModal: () => void
    basisType: BasisTypeBF
    onScrollEvent: (firstDateShown: string, lastDateShown: string) => void
    isCompanyBooking: boolean
}): ReactElement => {
    const dispatch = useAppDispatch()
    const competences =
        selectedGoal && selectedPartner
            ? selectedPartner
                  .competencesForGoal(selectedGoal)
                  .map((competence) => {
                      return {
                          icon: competence.picture,
                          title: competence.name,
                          content: competence.text,
                      }
                  })
            : []

    const reviews = selectedPartner
        ? selectedPartner.reviews.map((review) => ({
              name: review.name,
              comment: JSON.parse(review.comment),
              rating: review.rating,
              date: review.date.toString('dd/MM/yyyy'),
          }))
        : []

    const displayedProfiletext =
        selectedPartner && selectedGoal
            ? selectedPartner.profileTextForGoal(selectedGoal)
            : ''

    const partnerModalFooterButtons = selectedPartner ? (
        <Grid gap="4">
            {selectedDate && (
                <Flex gap="4">
                    <Text bold primary css={{ textTransform: 'capitalize' }}>
                        {`${selectedDate.toString(
                            'EEEE'
                        )} ${selectedDate.toString('d/M')}`}{' '}
                        -
                    </Text>
                    <Text bold primary>
                        kl.{' '}
                        {`${time ? time : t('calendar.modal.book.choosetime')}`}
                    </Text>
                </Flex>
            )}
            <Flex gap="8">
                <Button
                    onClick={onClickBook}
                    size="large"
                    variant="success"
                    data-cy="PartnerModalCTA"
                    disabled={!canClickNext}
                >
                    {selectedService
                        ? `${t('common:book').toUpperCase()}
                  ${
                      selectedService.duration.toMillis() / 60 / 1000
                  } min / ${selectedPartner.getPrice(selectedService)},-`
                        : t('common:book').toUpperCase()}
                </Button>
                {!isCompanyBooking && (
                    <Button
                        onClick={() => onClickKlippeKortButtonHeaderDesktop()}
                        size="large"
                        data-cy="modal_klippekort_button"
                    >
                        {t('calendar.package.btntext')}
                    </Button>
                )}
            </Flex>
        </Grid>
    ) : (
        <></>
    )

    const extraTabs = [
        ...(basisType.name === 'Training' ? personalTrainingTabsMaker(t) : []),
    ]

    return selectedPartner ? (
        <ProductModal
            activeTab={activeTab}
            bookTab={{
                carouselRef: partnerModalCarouselRef,
                dates: dates ?? [],
                datesToShow: isMobile ? 5 : 12,
                label: t(`calendar.modalnav.book`),
                onClickDate: (date) => {
                    if (selectedDate?.toString('yyyy-MM-dd') !== date) {
                        setDate(date)
                    }
                },
                onClickTimeSlot: (time) => setTime(time),
                onScrollEvent: onScrollEvent,
                showCloseIcon: false,
                timeSlots: timeSlots,
                timeText: t('calendar.modal.book.choosetime'),
            }}
            extraTabs={extraTabs}
            competencesTab={{
                competences: competences,
                hide: ![2, 3].includes(basisType.id),
                label: t(`calendar.modalnav.competences`),
            }}
            footerButtons={partnerModalFooterButtons}
            onClose={handleOnCloseModal}
            onOpen={handleOnOpenModal}
            onChangeTab={(tab) => dispatch(setActiveTab(tab))}
            open
            noReviewsText={t('common:newatraskrask')}
            numberOfReviews={selectedPartner.numberOfReviews}
            productImage={selectedPartner.therapistPictureLink}
            productName={selectedPartner.alias}
            profileTab={{
                educations: selectedPartner.education.split(','),
                educationText: t('calendar.profile.education', {
                    provider: t(
                        `common:basistypes.${basisType.id}.singularis`
                    ).toLowerCase(),
                }),
                label: t(`calendar.modalnav.profile`),
                profileText: selectedGoal && stripHTML(displayedProfiletext),
                readMoreText: (readMore) =>
                    t(`common:show${readMore ? 'less' : 'more'}`),
            }}
            rating={selectedPartner.rating}
            reviewsTab={{
                label: t(`calendar.modalnav.reviews`),
                noReviewsText: t('common:noreviews', {
                    person: selectedPartner.alias,
                }),
                ratingText: t('calendar.reviews.trustpilot'),
                reviews: reviews,
            }}
            type={isMobile ? 'drawer' : 'modal'}
        />
    ) : (
        <></>
    )
}
